import React, { useState } from 'react';
import Button from './Button';
import Text, { H1, H2, H3, Body, Caption } from './Text';
import Icon from './Icon';
import Input from './Input';
import Textarea from './Textarea';
import Card from './Card';
import Alert from './Alert';
import Modal from './Modal';

/**
 * EGGS Design System Test Page
 * Tests all component variants and responsive behavior
 */
export default function DesignSystemTest() {
  const [modalOpen, setModalOpen] = useState(false);
  const [bookingState, setBookingState] = useState('default');
  const [fakeBooking, setFakeBooking] = useState(false);
  
  return (
    <div className="p-8 max-w-6xl mx-auto space-y-12">

      {/* Booking Flow Components */}
      <section>
        <H2 className="mb-6">Booking Flow Components</H2>

        <div className="space-y-8">
          {/* State Switcher */}
          <div className="flex flex-wrap gap-2">
            {['default', 'loading', 'sold-out-error', 'generic-error', 'sold-out', 'not-found'].map((s) => (
              <Button
                key={s}
                size="sm"
                variant={bookingState === s ? 'brand' : 'outline'}
                onClick={() => { setBookingState(s); setFakeBooking(s === 'loading'); }}
              >
                {s}
              </Button>
            ))}
          </div>

          {/* Booking Confirmation Sheet */}
          {bookingState !== 'sold-out' && bookingState !== 'not-found' && (
            <div className="relative bg-gray-900/20 rounded-3xl p-4 sm:p-8 flex items-end sm:items-center justify-center min-h-[500px]">
              <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden animate-fade-in">
                {/* Header */}
                <div className="bg-eggs-yellow px-6 pt-6 pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 pr-4">
                      <Caption variant="sm" className="text-eggs-dark/60 mb-1">
                        Gondel-Session buchen
                      </Caption>
                      <H3 className="text-eggs-dark leading-tight">
                        Serverless Apps auf dem Riesenrad
                      </H3>
                    </div>
                    <button
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-eggs-dark/10 hover:bg-eggs-dark/20 transition-colors shrink-0"
                      aria-label="Schließen"
                    >
                      <span className="text-eggs-dark text-lg">✕</span>
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1">
                      <Body weight="Strong" className="text-eggs-dark">Max Mustermann</Body>
                      <Body variant="sm" className="text-gray-500">eggs unimedia GmbH</Body>
                    </div>
                    <div className="bg-gray-100 rounded-xl px-3 py-2 text-center shrink-0">
                      <Body variant="sm" weight="Strong" className="text-eggs-dark">14:00</Body>
                      <Caption variant="xs" className="text-gray-500">Uhr</Caption>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-xl px-4 py-2.5 mb-5 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full shrink-0" />
                    <Body variant="sm" weight="Strong" className="text-green-800">
                      3 Plätze verfügbar
                    </Body>
                  </div>

                  {bookingState === 'sold-out-error' && (
                    <div className="bg-red-50 rounded-xl px-4 py-3 mb-4">
                      <Body variant="sm" className="text-red-700">
                        Dieser Platz ist leider nicht mehr verfügbar. Die Verfügbarkeit wurde aktualisiert.
                      </Body>
                    </div>
                  )}
                  {bookingState === 'generic-error' && (
                    <div className="bg-red-50 rounded-xl px-4 py-3 mb-4">
                      <Body variant="sm" className="text-red-700">
                        Buchung fehlgeschlagen. Bitte versuche es erneut.
                      </Body>
                    </div>
                  )}

                  <div className="flex flex-col gap-3">
                    <Button variant="brand" size="lg" disabled={fakeBooking} className="min-h-[48px] w-full">
                      {fakeBooking ? 'Wird gebucht...' : '🎡 Platz buchen'}
                    </Button>
                    <Button variant="ghost" size="md" className="min-h-[48px] w-full">
                      Abbrechen
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sold Out State */}
          {bookingState === 'sold-out' && (
            <div className="relative bg-gray-100 rounded-3xl p-8 flex items-center justify-center min-h-[400px]">
              <div className="bg-white rounded-3xl shadow-lg p-8 max-w-sm w-full text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <H2 className="text-red-500">✕</H2>
                </div>
                <H3 className="mb-2">Ausgebucht</H3>
                <Body className="text-gray-500 mb-6">
                  Diese Gondel-Session hat leider keine freien Plätze mehr.
                </Body>
                <Button variant="secondary" size="md" className="min-h-[48px] w-full">
                  ← Zurück
                </Button>
              </div>
            </div>
          )}

          {/* Not Found State */}
          {bookingState === 'not-found' && (
            <div className="relative bg-gray-100 rounded-3xl p-8 flex items-center justify-center min-h-[400px]">
              <div className="bg-white rounded-3xl shadow-lg p-8 max-w-sm w-full text-center">
                <H3 className="mb-2">Gondel nicht gefunden</H3>
                <Body className="text-gray-500 mb-6">
                  Diese Session existiert leider nicht.
                </Body>
                <Button variant="secondary" size="md" className="min-h-[48px] w-full">
                  ← Zurück zu den Gondel-Sessions
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>
      {/* Typography Tests */}
      <section>
        <H2 className="mb-6">Typography System</H2>
        
        <div className="space-y-4">
          <H1>Heading 1 - Main Title</H1>
          <H2>Heading 2 - Section Title</H2>
          <H3>Heading 3 - Subsection</H3>
          <Text type="Heading" variant="h4">Heading 4 - Component Title</Text>
          <Text type="Heading" variant="h5">Heading 5 - Small Title</Text>
          <Text type="Heading" variant="h6">Heading 6 - Micro Title</Text>
          
          <Body variant="lg">Body Large - Lorem ipsum dolor sit amet, consectetur adipiscing elit.</Body>
          <Body>Body Base - Default body text for most content areas.</Body>
          <Body variant="sm">Body Small - Secondary information and details.</Body>
          <Body variant="xs">Body Extra Small - Fine print and metadata.</Body>
          
          <Body weight="Strong">Body Strong - Emphasized content that needs attention.</Body>
          
          <Caption variant="lg">Caption Large - Section Labels</Caption>
          <Caption>Caption Base - Default Labels</Caption>
          <Caption variant="sm">Caption Small - Micro Labels</Caption>
          <Caption variant="xs">Caption Extra Small - Tiny Labels</Caption>
        </div>
      </section>

      {/* Button Tests */}
      <section>
        <H2 className="mb-6">Button System</H2>
        
        {/* Size Tests */}
        <div className="space-y-6">
          <div>
            <H3 className="mb-4">Button Sizes</H3>
            <div className="flex flex-wrap gap-4 items-center">
              <Button size="lg">Large Button</Button>
              <Button size="md">Medium Button</Button>
              <Button size="sm">Small Button</Button>
            </div>
          </div>

          {/* Variant Tests */}
          <div>
            <H3 className="mb-4">Button Variants</H3>
            <div className="flex flex-wrap gap-4">
              <Button variant="brand">Brand</Button>
              <Button variant="primary">Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="inverse">Inverse</Button>
            </div>
          </div>

          {/* Icon Button Tests */}
          <div>
            <H3 className="mb-4">Buttons with Icons</H3>
            <div className="flex flex-wrap gap-4">
              <Button hasLeadingIcon leadingIcon="arrow-left">Back</Button>
              <Button hasTrailingIcon trailingIcon="arrow-right">Continue</Button>
              <Button hasLeadingIcon leadingIcon="plus">Add Item</Button>
              <Button hasTrailingIcon trailingIcon="chevron-down" variant="outline">Menu</Button>
            </div>
          </div>

          {/* Disabled State */}
          <div>
            <H3 className="mb-4">Disabled States</H3>
            <div className="flex flex-wrap gap-4">
              <Button disabled>Disabled Brand</Button>
              <Button variant="primary" disabled>Disabled Primary</Button>
              <Button variant="outline" disabled>Disabled Outline</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Icon Tests */}
      <section>
        <H2 className="mb-6">Icon System</H2>
        
        <div className="space-y-6">
          <div>
            <H3 className="mb-4">Icon Sizes</H3>
            <div className="flex items-center gap-4">
              <Icon name="star" size={16} />
              <Icon name="star" size={20} />
              <Icon name="star" size={24} />
              <Icon name="star" size={32} />
              <Icon name="star" size={48} />
            </div>
          </div>

          <div>
            <H3 className="mb-4">Common Icons</H3>
            <div className="grid grid-cols-8 gap-4">
              {[
                'arrow-right', 'arrow-left', 'chevron-right', 'chevron-down',
                'user', 'users', 'settings', 'menu', 'x', 'search',
                'bell', 'message-circle', 'calendar', 'home', 'star',
                'check', 'check-circle', 'alert-circle', 'info', 'plus',
                'edit', 'trash-2'
              ].map(iconName => (
                <div key={iconName} className="flex flex-col items-center gap-2 p-2">
                  <Icon name={iconName} size={24} />
                  <Caption variant="xs">{iconName}</Caption>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Form Components - Phase 2 */}
      <section>
        <H2 className="mb-6">Form Components (Phase 2)</H2>
        
        <div className="space-y-6 max-w-2xl">
          <Input 
            label="Email Address" 
            type="email"
            placeholder="your@email.com"
            helperText="We'll never share your email"
            required
          />
          
          <Input 
            label="Password" 
            type="password"
            placeholder="Enter password"
            error="Password must be at least 8 characters"
          />
          
          <Textarea 
            label="Message"
            placeholder="Type your message here..."
            rows={4}
            helperText="Maximum 500 characters"
          />
        </div>
      </section>

      {/* Card Components - Phase 2 */}
      <section>
        <H2 className="mb-6">Card Components (Phase 2)</H2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card 
            title="Default Card"
            subtitle="Standard card with border"
          >
            <Body>This is a default card with white background and subtle border.</Body>
          </Card>
          
          <Card 
            title="Elevated Card"
            subtitle="Card with shadow"
            variant="elevated"
          >
            <Body>This card has a shadow for emphasis.</Body>
          </Card>
          
          <Card 
            title="Outlined Card"
            subtitle="Bold border card"
            variant="outlined"
          >
            <Body>This card has a bold EGGS dark border.</Body>
          </Card>
          
          <Card 
            title="Filled Card"
            subtitle="Gray background"
            variant="filled"
            footer={
              <div className="flex gap-2">
                <Button size="sm" variant="outline">Cancel</Button>
                <Button size="sm">Confirm</Button>
              </div>
            }
          >
            <Body>This card has a light gray background with footer actions.</Body>
          </Card>
        </div>
      </section>

      {/* Alert Components - Phase 2 */}
      <section>
        <H2 className="mb-6">Alert Components (Phase 2)</H2>
        
        <div className="space-y-4 max-w-2xl">
          <Alert variant="success" title="Success!">
            Your changes have been saved successfully.
          </Alert>
          
          <Alert variant="error" title="Error">
            There was a problem processing your request.
          </Alert>
          
          <Alert variant="warning" title="Warning">
            This action cannot be undone. Please proceed with caution.
          </Alert>
          
          <Alert variant="info" title="Information">
            The event will start in 30 minutes. Please be ready.
          </Alert>
          
          <Alert variant="success" onClose={() => alert('Alert closed')}>
            Dismissible alert with close button.
          </Alert>
        </div>
      </section>

      {/* Modal Component - Phase 2 */}
      <section>
        <H2 className="mb-6">Modal Component (Phase 2)</H2>
        
        <div className="flex gap-4">
          <Button onClick={() => setModalOpen(true)}>Open Modal</Button>
        </div>
      </section>

      {/* Responsive Test */}
      <section>
        <H2 className="mb-6">Responsive Behavior</H2>
        <div className="space-y-4">
          <Body>Resize the window to test responsive typography scaling.</Body>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded">
              <H3 className="mb-2">Mobile First</H3>
              <Body>Text scales appropriately across breakpoints.</Body>
            </div>
            <div className="p-4 bg-gray-50 rounded">
              <H3 className="mb-2">Tablet View</H3>
              <Body>Medium screens maintain readability.</Body>
            </div>
            <div className="p-4 bg-gray-50 rounded">
              <H3 className="mb-2">Desktop View</H3>
              <Body>Large screens show full typography scale.</Body>
            </div>
          </div>
        </div>
      </section>

      {/* Color Test */}
      <section>
        <H2 className="mb-6">Color System</H2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-[#FEC626] text-[#35333A] rounded">
            <Body weight="Strong">EGGS Yellow</Body>
            <Caption>#FEC626</Caption>
          </div>
          <div className="p-4 bg-[#35333A] text-white rounded">
            <Body weight="Strong">EGGS Dark</Body>
            <Caption>#35333A</Caption>
          </div>
          <div className="p-4 bg-[#EFECEC] text-[#35333A] rounded">
            <Body weight="Strong">EGGS Light</Body>
            <Caption>#EFECEC</Caption>
          </div>
          <div className="p-4 bg-white text-[#35333A] border border-gray-200 rounded">
            <Body weight="Strong">White</Body>
            <Caption>#FFFFFF</Caption>
          </div>
        </div>
      </section>

      {/* Modal Instance */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Example Modal"
        footer={
          <>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={() => setModalOpen(false)}>Confirm</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Body>This is an example modal dialog with EGGS styling.</Body>
          <Input label="Name" placeholder="Enter your name" />
          <Textarea label="Comments" placeholder="Add your comments..." rows={3} />
        </div>
      </Modal>
    </div>
  );
}